/* eslint-disable max-lines */
import 'react-intl-tel-input/dist/main.css';

import type { RegionOption } from '@main/core-ui';
import {
  ContentErrorBoundary,
  Form,
  GenericFormattedMessage,
  ReactSelect,
  SelectRegion,
  SelectTagWrapper,
  useBreakpoint,
  useForm,
  validatorMessages,
} from '@main/core-ui';
import type {
  Action,
  FormItem,
  RegionInput,
  SelectOption,
} from '@main/datamap-types';
import { FormType, PostCompileStatus } from '@main/datamap-types';
import { Button } from '@main/pc-core-components/src/Button';
import {
  ButtonWrapper,
  ErrorWrapper,
  OuterErrorWrapper,
  SuccessWrapper,
} from '@main/pc-core-components/src/ContentWrapper';
import type { PrivacyCenterWorkflowConfig } from '@main/pc-types';
import type { ID } from '@main/schema-utils';
import { sp } from '@main/theme';
import { isEmptyStr } from '@main/utils';
import type { RequestAction } from '@transcend-io/privacy-types';
import { RegionDetectionMethod } from '@transcend-io/privacy-types';
import React, { useMemo, useState } from 'react';
import type { ValidationRule } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import IntlTelInput from 'react-intl-tel-input';

import { ActionButton } from '../ActionButton';
import { PanelWrapper } from '../DisplayActions/wrappers';
import {
  Description,
  StyledCheckbox,
  StyledInput,
  StyledTextArea,
} from './components';
import { FormItemWrapper, LabelWrapper } from './components/wrappers';
import { dataSubjectRequestFormMessages } from './messages';
import { NativeRegionSelect } from './NativeRegionSelect';
import {
  DataSubjectRequestFormWrapper,
  PostCompileWrapper,
  RegionDropdownWrapper,
  RequiredWrapper,
} from './wrappers';

export * from './messages';

/**
 * The output from the DSR Formik Form
 */
export interface FormOutput {
  /** The google ReCaptcha value */
  captcha?: string;
  /** The email address */
  email?: string;
  /** The name of the data subject type */
  subjectType: string;
  /** The type of request */
  type: RequestAction;
  /** Region option */
  region?: RegionOption;
  /** Form item details */
  details?: string;
  /** The additional actions to include int the request */
  additionalActions?: RequestAction[];
  /** The remaining form items are custom attributes */
  [x: string]: any;
}

/**
 * Props
 */
export interface DataSubjectRequestFormProps {
  /**
   * Function to handle form submission, see https://github.com/jaredpalmer/formik#handleSubmit-values-values-formikbag-formikbag--void
   *
   * Note that `values` has been extended to include the selected action type, e.g. {...values: type: selectedActionType}.
   */
  onSubmit: (formOutput: FormOutput) => void;
  /** The current DSR action is selected in the form */
  activeAction: Action;
  /** The workflow that was selected */
  activeWorkflow?: PrivacyCenterWorkflowConfig;
  /** The classification of data making the request */
  subjectType: string;
  /** Core identifier of user submitting request */
  coreIdentifier: string;
  /** A general error (JSX.Element) to render in the form if it has occurred, such as a network error */
  generalError?: React.ReactNode;
  /**
   * ID of the active data subject
   * TODO: https://linear.app/transcend/issue/BOW-3520 - not needed when workflows v2 setup
   */
  activeDataSubjectId: ID<'subject'>;
  /** The node to render on success */
  successMessage?: React.ReactNode;
}

/**
 * Construct the component from the form item config
 */
const DataSubjectRequestFormItem: React.FC<FormItem> = (formItem: FormItem) => {
  const { formatMessage } = useIntl();
  const { control } = useFormContext();
  const { type } = formItem;
  const [searchText, setSearchText] = useState('');
  const regexValidator: ValidationRule<RegExp> | undefined =
    // TODO: https://linear.app/transcend/issue/WAL-5396 - use regex validator
    // eslint-disable-next-line no-constant-condition
    false && formItem.validator?.pattern
      ? {
          value: new RegExp(formItem.validator!.pattern!),
          message:
            formItem.validator?.message ||
            `Failed to match pattern: ${formItem.validator!.pattern!}`,
        }
      : undefined;
  const optionValues = useMemo(
    () =>
      (
        formItem.options?.filter(({ name }) =>
          !searchText ? true : name.toLowerCase().includes(searchText),
        ) || []
      ).map((value) => ({
        ...value,
        value: value.name,
      })),
    [formItem.options],
  );
  switch (type) {
    case FormType.String:
      return (
        <Controller
          key={formItem.name}
          name={formItem.name}
          control={control}
          rules={
            formItem.isRequired
              ? { required: true, pattern: regexValidator }
              : { pattern: regexValidator }
          }
          render={({ field: { onChange, ref, ...props } }) => {
            const { value, ...rest } = props;
            return formItem.name === 'phone' ? (
              <div
                style={{
                  width: '100%',
                }}
              >
                <div>
                  <LabelWrapper>
                    {formatMessage(formItem.label)}
                    {formItem.isRequired && (
                      <RequiredWrapper>*</RequiredWrapper>
                    )}
                    {formItem.info && (
                      <Description
                        id={`${formItem.name}-description`}
                        descriptionHtml={formatMessage(formItem.info)}
                      />
                    )}
                  </LabelWrapper>
                </div>
                <IntlTelInput
                  containerClassName="intl-tel-input"
                  inputClassName={formItem.name}
                  placeholder={formItem.placeholder?.defaultMessage}
                  preferredCountries={['us']}
                  defaultValue={value}
                  {...rest}
                  style={{
                    width: 'fit-content',
                    borderRadius: sp(0.75),
                    marginRight: 'auto',
                    marginLeft: 'auto',
                    marginBottom: sp(2),
                  }}
                  onPhoneNumberChange={(status, value, countryData, number) => {
                    onChange(number);
                  }}
                />
              </div>
            ) : (
              <StyledInput
                label={formatMessage(formItem.label)}
                isRequired={formItem.isRequired}
                description={
                  formItem.info ? formatMessage(formItem.info) : undefined
                }
                {...(formItem.placeholder
                  ? { placeholder: formatMessage(formItem.placeholder) }
                  : {})}
                disabled={formItem.disabled}
                {...props}
                onChange={onChange}
              />
            );
          }}
        />
      );
    case FormType.TextArea:
      return (
        <Controller
          key={formItem.name}
          name={formItem.name}
          control={control}
          rules={
            formItem.isRequired
              ? { required: true, pattern: regexValidator }
              : { pattern: regexValidator }
          }
          render={({ field: { ref, ...props } }) => (
            <StyledTextArea
              label={formatMessage(formItem.label)}
              isRequired={formItem.isRequired}
              description={
                formItem.info ? formatMessage(formItem.info) : undefined
              }
              {...(formItem.placeholder
                ? { placeholder: formatMessage(formItem.placeholder) }
                : {})}
              disabled={formItem.disabled}
              {...props}
            />
          )}
        />
      );
    case FormType.Select:
      return (
        <Controller
          key={formItem.name}
          name={formItem.name}
          control={control}
          rules={
            formItem.isRequired
              ? { required: true, pattern: regexValidator }
              : { pattern: regexValidator }
          }
          render={({ field: { onChange, ref, ...props } }) => (
            <>
              <LabelWrapper>
                {formatMessage(formItem.label)}
                {formItem.isRequired && <RequiredWrapper>*</RequiredWrapper>}
                {formItem.info && (
                  <Description
                    descriptionHtml={formatMessage(formItem.info)}
                    id={`${formItem.name}-description`}
                  />
                )}
              </LabelWrapper>
              <SelectTagWrapper
                style={{ border: '1px solid black', borderRadius: sp(0.8) }}
              >
                <ReactSelect<false, SelectOption>
                  id={formItem.id}
                  options={optionValues}
                  value={
                    props.value
                      ? optionValues.find(
                          (option) => option.name === props.value,
                        )
                      : undefined
                  }
                  onInputChange={(newSearchText) =>
                    setSearchText(newSearchText)
                  }
                  style={{ border: '1px solid black' }}
                  getOptionLabel={(option) => formatMessage(option.label)}
                  isLoading={false}
                  showOutline={false}
                  onChange={(option) => {
                    if (option) {
                      onChange(option.name);
                    } else {
                      onChange(undefined);
                    }
                  }}
                />
              </SelectTagWrapper>
            </>
          )}
        />
      );
    case FormType.Boolean:
      return (
        <Controller
          key={formItem.name}
          name={formItem.name}
          control={control}
          rules={formItem.isRequired ? { required: true } : undefined}
          render={({ field: { onChange, ref, ...props } }) => (
            <StyledCheckbox
              label={formatMessage(formItem.label)}
              isRequired={formItem.isRequired}
              onChange={(e) => onChange(e.target.checked)}
              {...props}
            />
          )}
        />
      );
    default:
      return <></>;
  }
};

/**
 * Form item for short/expanded form access requests
 */
const FormItemAccessType: React.FC = () => {
  const { control } = useFormContext();
  return (
    <Controller
      key="postCompileStatus"
      name="postCompileStatus"
      control={control}
      rules={{ required: true }}
      render={({ field: { value, onChange } }) => (
        <PostCompileWrapper>
          <PanelWrapper>
            <ActionButton
              onClick={() => onChange(PostCompileStatus.ViewCategories)}
              title={dataSubjectRequestFormMessages.shortForm}
              description={dataSubjectRequestFormMessages.shortFormDescription}
              stepMessage={null}
              selected={value === PostCompileStatus.ViewCategories}
            />
            <ActionButton
              onClick={() => onChange(PostCompileStatus.Downloadable)}
              title={dataSubjectRequestFormMessages.expandedForm}
              description={
                dataSubjectRequestFormMessages.expandedFormDescription
              }
              stepMessage={null}
              selected={value === PostCompileStatus.Downloadable}
            />
          </PanelWrapper>
        </PostCompileWrapper>
      )}
    />
  );
};

/**
 * Form item for region
 */
const FormItemRegion: React.FC<{
  /** The list of regions to select from */
  regionList: string[];
}> = ({ regionList }) => {
  const { formatMessage } = useIntl();
  const { control } = useFormContext();
  const { isBeforeTablet } = useBreakpoint();
  return (
    <Controller
      key="region"
      name="region"
      control={control}
      rules={{
        validate: (value: RegionInput) =>
          !value ||
          Object.values(value).every(
            (val) =>
              isEmptyStr(val) ||
              (Array.isArray(val) && (val as any).length === 0),
          ) ? (
            <GenericFormattedMessage message={validatorMessages.required} />
          ) : (
            (undefined as any)
          ),
        required: true,
      }}
      render={({ field: { value, onChange } }) => (
        <FormItemWrapper>
          <LabelWrapper>
            {formatMessage(dataSubjectRequestFormMessages.region)}
            <RequiredWrapper>*</RequiredWrapper>
          </LabelWrapper>
          <RegionDropdownWrapper>
            {isBeforeTablet ? (
              <NativeRegionSelect
                allowedRegions={regionList}
                onChange={(region) => onChange(region)}
                value={value}
              />
            ) : (
              <SelectRegion
                isMulti={false}
                value={[value]}
                allowedRegions={regionList}
                onChange={([region]) => onChange(region)}
                style={{
                  width: '100%',
                }}
              />
            )}
          </RegionDropdownWrapper>
        </FormItemWrapper>
      )}
    />
  );
};

/**
 * A form for the data subject to submit a request
 */
export function DataSubjectRequestForm({
  generalError,
  successMessage,
  activeAction,
  activeWorkflow,
  onSubmit,
  activeDataSubjectId,
  subjectType,
  coreIdentifier,
}: DataSubjectRequestFormProps): JSX.Element {
  const { formatMessage } = useIntl();
  const formMethods = useForm({
    defaultValues: {
      region: {},
      ...activeAction.formItems.reduce(
        (acc, { name, type }) => ({
          ...acc,
          [name]: [FormType.String, FormType.TextArea].includes(type as any)
            ? ''
            : FormType.Boolean
              ? false
              : undefined,
        }),
        {},
      ),
      coreIdentifier,
      ...(activeAction.allowPostCompileChoice
        ? { postCompileStatus: activeAction.postCompileStatus }
        : {}),
    },
  });
  const { formState } = formMethods;
  const { isBeforeTablet } = useBreakpoint();
  return (
    <React.Fragment>
      <ContentErrorBoundary>
        <Form
          onSubmit={(values) =>
            onSubmit({
              ...values,
              type: activeAction.type,
              subjectType,
              regionDetectionMethod:
                activeAction.regionDetectionMethod ===
                RegionDetectionMethod.Form
                  ? values.region
                  : activeAction.regionDetectionMethod ===
                      RegionDetectionMethod.Auto
                    ? undefined //  TODO: https://linear.app/transcend/issue/WAL-5273
                    : undefined,
            })
          }
          useFormMethods={formMethods}
        >
          <DataSubjectRequestFormWrapper>
            <div
              style={{
                alignSelf: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: isBeforeTablet ? '320px' : sp(46),
              }}
            >
              {!successMessage && (
                <>
                  {/** Expose short/expanded form access requests */}
                  {activeAction.allowPostCompileChoice && (
                    <FormItemAccessType />
                  )}
                  {activeAction.formItems
                    // TODO: https://linear.app/transcend/issue/BOW-3520 - not needed when workflows v2 setup
                    .filter(
                      (formItem) =>
                        (formItem.dataSubjectIds || []).length === 0 ||
                        (formItem.dataSubjectIds || []).includes(
                          activeDataSubjectId,
                        ),
                    )
                    .map((formItem) => (
                      <DataSubjectRequestFormItem
                        key={formItem.name}
                        {...formItem}
                      />
                    ))}
                  {/** Expose region selection */}
                  {activeAction.regionDetectionMethod ===
                    RegionDetectionMethod.Form && (
                    <FormItemRegion regionList={activeAction.regionList} />
                  )}
                </>
              )}
              {successMessage ? (
                <SuccessWrapper>{successMessage}</SuccessWrapper>
              ) : (
                <ButtonWrapper>
                  <Button
                    buttonType="primary"
                    type="submit"
                    disabled={formState.isSubmitting || !formState.isValid}
                    loading={formState.isSubmitting}
                    title={
                      formState.isValid
                        ? undefined
                        : formatMessage(
                            dataSubjectRequestFormMessages.buttonTitle,
                          )
                    }
                  >
                    {formatMessage(dataSubjectRequestFormMessages.submit)}
                  </Button>
                </ButtonWrapper>
              )}
              {generalError && (
                <OuterErrorWrapper>
                  <ErrorWrapper>{generalError}</ErrorWrapper>
                </OuterErrorWrapper>
              )}
            </div>
          </DataSubjectRequestFormWrapper>
        </Form>
      </ContentErrorBoundary>
    </React.Fragment>
  );
}

export default DataSubjectRequestForm;
/* eslint-enable max-lines */
